import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Divider,
    Typography
} from '@mui/material';
import { getSales } from '../../services/sales';
import Filters from './Filters';
import Loading from './Loading';
import { useTranslation } from 'react-i18next';

function SalesList({ user }) {

    const { t } = useTranslation();

    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [sales, setSales] = useState([]);
    const [filters, setFilters] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null); 

    const [startDate, setStartDate] = useState(() => {
        const today = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return today.toISOString().split('T')[0]; 
    });
    const [endDate, setEndDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [clickId, setClickId] = useState('');

    const [page, setPage] = useState(0); // Pagination: current page
    const [rowsPerPage, setRowsPerPage] = useState(10); // Pagination: rows per page

    const loadSales = async () => {
        setError(null);
        setIsLoading(true);
        try {
            const salesResponse = await getSales(user._id, startDate, endDate, selectedPlatforms.join(","), clickId);
            setSales(salesResponse.sales);
            setFilters(salesResponse.filters);
        } catch (error) {
            console.error('Error fetching sales:', error);
            setError(t('salesList.error'));
        } finally {
            setIsLoading(false);
            setLastUpdate(Date.now());
        }
    };

    useEffect(() => {
        loadSales();
    }, []);

    // Handle Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            {/* Filters Section */}
            <Filters
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                platforms={filters?.platforms}
                selectedPlatforms={selectedPlatforms}
                clickIds={filters?.clickIds}
                clickId={clickId}
                setClickId={setClickId}
                setSelectedPlatforms={setSelectedPlatforms}
                onReload={loadSales}
                lastUpdate={lastUpdate}
            />

            <Divider />

            {/* Table Section for larger screens */}
            <TableContainer                 
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    padding: 2
                }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell>{t('salesList.date')}</TableCell>
                            <TableCell>{t('salesList.campaign')}</TableCell>
                            <TableCell>{t('salesList.clickId')}</TableCell>
                            <TableCell>{t('salesList.commission')}</TableCell>
                            <TableCell>{t('salesList.platform')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sales
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) // Paginate data
                            .map((item) => {
                                return (
                                    (
                                        <TableRow key={item._id}>
                                            <TableCell>{new Date(item.timestamp).toLocaleString()}</TableCell>
                                            <TableCell sx={item.sale.campaignName ? {
                                                fontWeight: 'bold',
                                                color: 'black'
                                            } : {
                                                fontWeight: 'light',
                                                color: 'gray'
                                            }}>{item.sale.campaignName || "N/A"}</TableCell>
                                            <TableCell>{item.sale.clickId}</TableCell>
                                            <TableCell>{item.sale.amount} {item.sale.currency}</TableCell>
                                            <TableCell>{item.sale.platformName}</TableCell>
                                        </TableRow>
                                    )
                                )
                            })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Table Section for smaller screens */}
            <Box
                sx={{
                    display: { xs: 'block', md: 'none' },
                    padding: 2,
                    maxHeight: '50vh',
                    overflowY: 'auto',
                }}
                >
                {sales
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item) => (
                    <Box
                        key={item._id}
                        sx={{
                        border: '1px solid #ddd',
                        borderRadius: 2,
                        mb: 2,
                        p: 2,
                        boxShadow: 1,
                        backgroundColor: '#fff',
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        <strong>{t('salesList.date')}:</strong> {new Date(item.timestamp).toLocaleString()}
                        </Typography>
                        <Typography
                        variant="body2"
                        sx={{
                            fontWeight: item.sale.campaignName ? 'bold' : 'light',
                            color: item.sale.campaignName ? 'black' : 'gray',
                        }}
                        >
                        <strong>{t('salesList.campaign')}:</strong> {item.sale.campaignName || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                        <strong>{t('salesList.clickId')}:</strong> {item.sale.clickId}
                        </Typography>
                        <Typography variant="body2">
                        <strong>{t('salesList.commission')}:</strong> {item.sale.amount} {item.sale.currency}
                        </Typography>
                        <Typography variant="body2">
                        <strong>{t('salesList.platform')}:</strong> {item.sale.platformName}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Pagination Controls */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={sales.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        
            <Loading text={t('salesList.loading')} isLoading={isLoading} />
        </Box>
    );
}

export default SalesList;